<template>
  <div class="services-page">
    <!-- Hero Section -->
    <section class="hero">
      <div class="container">
        <h1>Our Services</h1>
        <p>Comprehensive solutions for international market entry and expansion.</p>
      </div>
    </section>

    <!-- Services Grid Section -->
    <section class="services-section">
      <div class="container">
        <div class="section-title">
          <h2>Comprehensive Market Entry Solutions</h2>
          <p>We provide end-to-end services to help your business successfully enter and thrive in international markets.</p>
        </div>
        
        <div class="services-grid">
          <ServiceCard 
            v-for="service in services" 
            :key="service.id"
            :service="service" />
        </div>
      </div>
    </section>

    <!-- Process Section -->
    <section class="process">
      <div class="container">
        <div class="section-title">
          <h2>Our Process</h2>
          <p>A structured approach to ensure your international expansion success.</p>
        </div>
        
        <div class="process-steps">
          <div class="process-step" v-for="(step, index) in processSteps" :key="index">
            <div class="step-number">{{ index + 1 }}</div>
            <div class="step-content">
              <h3>{{ step.title }}</h3>
              <p>{{ step.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-content">
          <h2>Ready to Expand Your Business Internationally?</h2>
          <p>Contact us today to discuss how we can help you enter new markets and grow your business globally.</p>
          <div class="cta-buttons">
            <router-link to="/contact" class="btn-primary">Get Started</router-link>
            <button class="btn-secondary" @click="scrollToDiagnosis">Try Our Diagnosis Tool</button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import ServiceCard from '../components/modals/shared/ServiceCard.vue'

export default {
  name: 'Services',
  components: {
    ServiceCard
  },
  data() {
    return {
      services: [
        {
          id: 'market-diagnosis',
          title: 'Market Diagnosis',
          description: 'Input your industry, product, and objectives to get recommendations for the most suitable countries and cities.',
          icon: 'fas fa-search',
          features: [
            'Get recommended countries & cities',
            'Receive rationale & risk assessment',
            'Customized market insights',
            'Competitive analysis'
          ]
        },
        {
          id: 'market-entry',
          title: 'Market Entry & Inspection',
          description: 'Comprehensive support for entering new markets with policy interpretation and compliance registration.',
          icon: 'fas fa-plane',
          features: [
            'Policy interpretation',
            'Compliance registration',
            'Industrial park recommendations',
            'Itinerary arrangement',
            'Local partner identification'
          ]
        },
        {
          id: 'partnership',
          title: 'Partnership Facilitation',
          description: 'Connect with reliable partners through our extensive enterprise resource pool.',
          icon: 'fas fa-handshake',
          features: [
            'Supply chain procurement',
            'Channel/agent partnerships',
            'ESB cooperation',
            'Access to enterprise resource pool',
            'Contract negotiation support'
          ]
        },
        {
          id: 'logistics',
          title: 'Logistics & Supply Chain',
          description: 'End-to-end logistics solutions to ensure smooth operations in your target markets.',
          icon: 'fas fa-shipping-fast',
          features: [
            'Customs clearance assistance',
            'Warehousing solutions',
            'Distribution network setup',
            'Supply chain optimization',
            'Risk management'
          ]
        },
        {
          id: 'compliance',
          title: 'Legal & Compliance',
          description: 'Navigate complex regulatory environments with our expert legal and compliance support.',
          icon: 'fas fa-gavel',
          features: [
            'Regulatory compliance',
            'Contract drafting & review',
            'Intellectual property protection',
            'Dispute resolution',
            'Local law expertise'
          ]
        },
        {
          id: 'training',
          title: 'Cultural Training',
          description: 'Prepare your team for success with cultural awareness and business etiquette training.',
          icon: 'fas fa-users',
          features: [
            'Cultural awareness programs',
            'Business etiquette training',
            'Language support services',
            'Cross-cultural negotiation skills',
            'Market-specific workshops'
          ]
        }
      ],
      processSteps: [
        {
          title: 'Initial Consultation',
          description: 'We begin by understanding your business, goals, and requirements for international expansion.'
        },
        {
          title: 'Market Diagnosis',
          description: 'Our experts analyze potential markets and provide data-driven recommendations.'
        },
        {
          title: 'Strategy Development',
          description: 'We create a customized market entry strategy tailored to your specific needs.'
        },
        {
          title: 'Implementation Support',
          description: 'Our team provides hands-on support throughout the market entry process.'
        },
        {
          title: 'Partnership Facilitation',
          description: 'We connect you with reliable local partners, suppliers, and distributors.'
        },
        {
          title: 'Ongoing Support',
          description: 'We continue to support your business with market monitoring and expansion assistance.'
        }
      ]
    }
  },
  methods: {
    scrollToDiagnosis() {
      // 在实际应用中，这里应该滚动到诊断工具部分
      // 这里简单跳转到首页的诊断部分
      this.$router.push('/')
    }
  }
}
</script>

<style scoped>
.hero {
  background: linear-gradient(rgba(26, 58, 95, 0.85), rgba(26, 58, 95, 0.9)), url('https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80');
  background-size: cover;
  background-position: center;
  color: white;
  padding: 80px 0;
  text-align: center;
}

.hero h1 {
  font-size: 42px;
  margin-bottom: 20px;
}

.hero p {
  font-size: 18px;
  max-width: 700px;
  margin: 0 auto 30px;
}

.services-section {
  padding: 80px 0;
  background-color: white;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
}

.process {
  padding: 80px 0;
  background-color: #f5f7fa;
}

.process-steps {
  max-width: 800px;
  margin: 0 auto;
}

.process-step {
  display: flex;
  align-items: flex-start;
  margin-bottom: 40px;
}

.step-number {
  background: linear-gradient(135deg, #4dabf7 0%, #1a3a5f 100%);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  margin-right: 20px;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-content h3 {
  color: #1a3a5f;
  margin-bottom: 10px;
  font-size: 22px;
}

.step-content p {
  line-height: 1.6;
}

.cta-section {
  padding: 80px 0;
  background-color: white;
}

.cta-content {
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
}

.cta-content h2 {
  color: #1a3a5f;
  margin-bottom: 20px;
  font-size: 32px;
}

.cta-content p {
  margin-bottom: 30px;
  font-size: 18px;
}

.cta-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
}

@media (max-width: 768px) {
  .hero h1 {
    font-size: 32px;
  }
  
  .hero p {
    font-size: 16px;
  }
  
  .services-section, .process, .cta-section {
    padding: 50px 0;
  }
  
  .services-grid {
    grid-template-columns: 1fr;
  }
  
  .process-step {
    flex-direction: column;
    text-align: center;
  }
  
  .step-number {
    margin-right: 0;
    margin-bottom: 15px;
  }
  
  .cta-buttons {
    flex-direction: column;
  }
}
</style>